/**
 * NotificationPreferences — iter49
 * Compact panel embedded on the Settings page. Currently surfaces the
 * two "digest & nudge" toggles because those are the only ones the
 * pipeline actively uses. Adding more prefs is a matter of extending
 * ROWS below; the panel auto-hydrates from `GET /users/me/notification-prefs`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Bell, Mail, AlarmClock, Send, Sparkles, Sunrise } from "lucide-react";

const ROWS = [
  {
    key: "daily_digest",
    icon: Sunrise,
    label: "Günlük özet e-postası",
    hint: "Her sabah 08:00 (senin saat diliminde) — açık ve gecikmiş görevlerinin özeti. Görevin yoksa e-posta gönderilmez.",
  },
  {
    key: "weekly_digest",
    icon: Mail,
    label: "Haftalık özet e-postası",
    hint: "Cuma 17:00 (senin saat diliminde) — bu haftaki bitişler + gelecek hafta öncelikleri.",
  },
  {
    key: "task_nudge",
    icon: AlarmClock,
    label: "Görev hatırlatıcısı (Nudge)",
    hint: "3 gün açık kalan görevlerine kibar bir DM. 5 gün sonra yönetici e-posta ile bilgilendirilir.",
  },
];

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    api
      .get("/users/me/notification-prefs")
      .then((r) => setPrefs(r.data || {}))
      .catch((e) =>
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Tercihler yüklenemedi",
        ),
      );
  }, []);

  const togglePref = async (key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    setSaving(true);
    try {
      await api.patch("/users/me/notification-prefs", { prefs: { [key]: value } });
      toast.success("Tercih güncellendi");
    } catch (e) {
      // Rollback on failure.
      setPrefs((prev) => ({ ...prev, [key]: !value }));
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi",
      );
    } finally {
      setSaving(false);
    }
  };

  const sendMyDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/digest/me/now");
      toast.success(
        `Digest e-postası kuyruğa alındı (bu hafta: ${data.summary.completed} bitmiş / ${data.summary.overdue_count} gecikmiş)`,
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Gönderilemedi",
      );
    } finally {
      setSendingDigest(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/digest/preview");
      setPreview(data);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Önizleme yüklenemedi",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  if (!prefs) {
    return (
      <div className="max-w-2xl rounded-lg border border-zinc-200 bg-white p-6 text-sm text-zinc-500">
        Tercihler yükleniyor…
      </div>
    );
  }

  return (
    <section className="max-w-2xl space-y-4 rounded-lg border border-zinc-200 bg-white p-6">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-zinc-500" strokeWidth={2} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
          Bildirim Tercihleri
        </h2>
      </div>
      <div className="divide-y divide-zinc-100">
        {ROWS.map((row) => {
          const Icon = row.icon;
          const value = prefs[row.key] !== false; // default: on
          return (
            <div
              key={row.key}
              className="flex items-start gap-3 py-3"
              data-testid={`pref-row-${row.key}`}
            >
              <Icon className="h-4 w-4 mt-1 text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900">
                  {row.label}
                </div>
                <div className="mt-0.5 text-xs text-zinc-500">{row.hint}</div>
              </div>
              <Switch
                checked={value}
                onCheckedChange={(v) => togglePref(row.key, v)}
                disabled={saving}
                data-testid={`pref-toggle-${row.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-4">
        <Button
          type="button"
          onClick={sendMyDigest}
          disabled={sendingDigest}
          data-testid="pref-send-my-digest"
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          size="sm"
        >
          <Send className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
          {sendingDigest ? "Gönderiliyor…" : "Digest'imi şimdi gönder"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={loadPreview}
          disabled={loadingPreview}
          data-testid="pref-preview-digest"
          size="sm"
        >
          <Sparkles className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
          {loadingPreview ? "Yükleniyor…" : "Önizle"}
        </Button>
      </div>

      {preview && (
        <div
          data-testid="pref-digest-preview-panel"
          className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 p-4"
        >
          <div className="text-xs uppercase tracking-wider text-zinc-500">
            Bu hafta senin özet
          </div>
          <div className="mt-1 text-sm font-semibold text-zinc-900">
            {preview.subject}
          </div>
          <ul className="mt-3 grid grid-cols-2 gap-2 text-sm text-zinc-700">
            <li>
              ✅ <b>{preview.completed}</b> bu hafta bitmiş
            </li>
            <li>
              📋 <b>{preview.open}</b> açık
            </li>
            <li>
              ⚠️ <b>{preview.overdue_count}</b> gecikmiş
            </li>
            <li>
              🗓️ <b>{preview.next_week_top?.length || 0}</b> gelecek hafta due
            </li>
          </ul>
          {preview.overdue_top?.length ? (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Gecikenler
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-700">
                {preview.overdue_top.map((t) => (
                  <li key={t.id}>
                    • {t.title}{" "}
                    <span className="text-zinc-400">
                      ({(t.due_at || "").slice(0, 10)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.next_week_top?.length ? (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Gelecek hafta
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-700">
                {preview.next_week_top.map((t) => (
                  <li key={t.id}>
                    • {t.title}{" "}
                    <span className="text-zinc-400">
                      ({(t.due_at || "").slice(0, 10)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
